/**
 * Utility for Google Tag manager related functions
 */

import { transformAuth0IntoQwaSession } from '../auth/session';
import { QwaUser } from '../auth/types';
import { getTestDriveDomain, isTestDriveUser } from './services/authActions';

/**
 * Performs no validation, assumes valid e-mail address
 * @param emailAddress String with 1 `@` character
 * @returns emailAddress that has only the 1st 3 characters for the name intact
 */
export const anonymizeEmailAddress = (emailAddress: string): string => {
  const [name, domain] = emailAddress.split('@');
  const numOfNameCharsToUse = name.length > 3 ? 3 : name.length;
  const truncatedName = name.substring(0, numOfNameCharsToUse);

  const anonymizedEmailAddress = truncatedName + '@' + domain;
  return anonymizedEmailAddress;
};

export const GTMPageView = async (url: string, user) => {
  const qwaUserSession = user ? transformAuth0IntoQwaSession(user) : undefined;
  const testDriveUser = await isTestDriveUser(qwaUserSession);

  let pageEvent: Record<string, unknown> = {
    event: 'pageview',
    page: url,
    test_drive_user: false,
  };

  // there are 2 scenarios where we should have GTM enabled:
  // - a test drive user is using the app
  // - someone is viewing non-app pages, i.e. the homepage
  const isUsingApp = url.includes('/app');
  const isGtmInitialized = Boolean(window && window.dataLayer);

  if (typeof url === 'string' && isGtmInitialized && (!isUsingApp || testDriveUser)) {
    // add additional info about the test drive user
    if (testDriveUser) {
      if (!user) {
        return pageEvent;
      }
      const testDriveDomain = await getTestDriveDomain();

      const qwaUser = qwaUserSession?.user || { name: '', email: '' };

      const { name, email } = qwaUser;

      const anonymizedEmailAddress = email ? anonymizeEmailAddress(email) : email;

      // rename some fields to be more verbose/descriptive
      pageEvent = {
        ...pageEvent,
        test_drive_user: true,
        full_name: name,
        email: anonymizedEmailAddress,
        domain: testDriveDomain,
        // TODO we lost this information, after upgrading our Okta integration, is any of it still useful?
        // expiration_timestamp: exp,
        // issued_at_timestamp: iat,
        // token_id: id,
      };
    }

    window.dataLayer.push(pageEvent);
  }

  return pageEvent;
};

export const ScheduleDemoEvent = () => {
  // demoRequestWithMessage tag is paused for now!
  const pageEvent = {
    event: 'demoRequestWithoutMessage',
  };

  window && window.dataLayer && window.dataLayer.push(pageEvent);
};

export const testDriveSubmitEvent = () => {
  const pageEvent = {
    event: 'TestDriveRequestSubmit',
  };

  window && window.dataLayer && window.dataLayer.push(pageEvent);
};

export const triggerLightboxOpenedEvent = (isTestDrive = false) => {
  const pageEvent = {
    event: isTestDrive ? 'TestDriveLightboxOpened' : 'RequestDemoLighboxOpened',
  };

  window && window.dataLayer && window.dataLayer.push(pageEvent);
};

export const initPageView = () => {
  const pageEvent = {
    'Click ID': 'cookie-accept',
  };

  window && window.dataLayer && window.dataLayer.push(pageEvent);
};

// TODO link Quora doc here
export const addQuoraTags = () => {
  // @ts-expect-error Code is exactly per Quora's doc
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  !(function (q, e, v, n, t, s) {
    // @ts-expect-error Code is exactly per Quora's doc
    if (q.qp) return;

    // @ts-expect-error Code is exactly per Quora's doc
    n = q.qp = function () {
      // @ts-expect-error Code is exactly per Quora's doc
      // eslint-disable-next-line prefer-spread, prefer-rest-params
      n.qp ? n.qp.apply(n, arguments) : n.queue.push(arguments);
    };
    // @ts-expect-error Code is exactly per Quora's doc
    n.queue = [];
    // @ts-expect-error Code is exactly per Quora's doc
    t = document.createElement(e);
    // @ts-expect-error Code is exactly per Quora's doc
    t.async = !0;
    // @ts-expect-error Code is exactly per Quora's doc
    t.src = v;
    // @ts-expect-error Code is exactly per Quora's doc
    s = document.getElementsByTagName(e)[0];
    // @ts-expect-error Code is exactly per Quora's doc
    s.parentNode.insertBefore(t, s);
  })(window, 'script', 'https://a.quora.com/qevents.js');
  // @ts-expect-error Code is exactly per Quora's doc
  if (window.qp) {
    // @ts-expect-error Code is exactly per Quora's doc
    window.qp('init', '53f150d0cdee4fb5be4a4054f479c950');
    // @ts-expect-error Code is exactly per Quora's doc
    window.qp('track', 'ViewContent');

    const s = document.getElementsByTagName('script')[0];
    const b = document.createElement('noscript');
    const ig = document.createElement('img');
    // @ts-expect-error Code is exactly per Quora's doc
    ig.height = '1';
    // @ts-expect-error Code is exactly per Quora's doc

    ig.width = '1';
    // @ts-expect-error Code is exactly per Quora's doc

    ig.style = 'display:none';
    ig.src = 'https://q.quora.com/_/ad/53f150d0cdee4fb5be4a4054f479c950/pixel?tag=ViewContent&noscript=1';
    b.appendChild(ig);
    s.parentNode?.insertBefore(b, s);
  }
};
